import React from "react";
import ServicePage from "./ServicePage";

export default function PerformanceSeo() {
  return (
    <ServicePage
      slug="performance-seo"
      eyebrow="Service · 04"
      title="Performance"
      titleAccent="& SEO."
      intro="Core Web Vitals tuning, structured data, schema markup and technical SEO. Real numbers in production — sub-second TTFB, clean Lighthouse, organic traffic that actually compounds."
      capabilities={[
        {
          title: "Performance audit + plan",
          desc: "A written audit grounded in real data — CrUX, RUM, Lighthouse, server logs. Prioritized list of fixes by ROI, not by what's easy.",
          points: [
            "Core Web Vitals breakdown (LCP, INP, CLS) on real traffic",
            "Critical render path + JS budget analysis",
            "Server-side timing (TTFB, DB, render)",
          ],
        },
        {
          title: "Front-end optimization",
          desc: "Images, fonts, scripts, hydration. The unglamorous fixes that move the needle.",
          points: [
            "Responsive image pipeline + AVIF/WebP",
            "Font subsetting and FOIT/FOUT control",
            "Third-party script hygiene + tag manager pruning",
          ],
        },
        {
          title: "Technical SEO",
          desc: "Crawlability, indexability, structured data, internal linking. The plumbing search engines reward.",
          points: [
            "Sitemap, robots, canonical strategy",
            "Schema.org (Product, Article, FAQ, Breadcrumb, etc.)",
            "Search Console error triage and fix-loop",
          ],
        },
        {
          title: "Backend & infra perf",
          desc: "Caching layers, query plans, edge runtime, image CDN. Where the real wins often hide.",
          points: [
            "Edge caching + ISR / streaming",
            "DB query plans + missing indexes",
            "CDN + Brotli + sane cache headers",
          ],
        },
      ]}
      process={[
        { title: "Measure", desc: "RUM and synthetic baselines on the pages that drive revenue. No guessing." },
        { title: "Prioritize", desc: "Rank fixes by impact × effort. Skip the low-impact theater." },
        { title: "Ship", desc: "Each fix behind a flag, measured against the baseline before it ships fully." },
        { title: "Monitor", desc: "Dashboards that the team will actually look at. Alerts on regressions, not vanity." },
      ]}
      outcomes={[
        { kpi: "Web Vitals", title: "Green CrUX, all pages", desc: "Mobile and desktop, on real visitors — not lab cherry-picks." },
        { kpi: "Search", title: "Indexable & rich", desc: "Schema markup that earns rich results; clean Search Console." },
        { kpi: "Revenue", title: "Conversion lift", desc: "Faster pages convert better. Boring, repeatedly true." },
      ]}
      techHighlights={[
        "Core Web Vitals (LCP, INP, CLS)",
        "Lighthouse / WebPageTest",
        "Schema.org + JSON-LD",
        "Sitemaps & robots.txt",
        "Cloudflare / Cloudfront",
        "Image CDNs (Shopify, Cloudinary, Imgix)",
        "Search Console + GA4",
        "RUM (web-vitals)",
      ]}
      faqs={[
        {
          q: "How long does it take to see results?",
          a: "Performance — within the same sprint. Vitals respond to code changes in days. SEO — slower. Indexing changes show up in 1–2 weeks; ranking shifts from technical fixes typically take 4–12 weeks. Anyone promising overnight rankings is selling something.",
        },
        {
          q: "Do you do content / link building?",
          a: "No. I'm a technical SEO and performance engineer. I make sure your content can be found and your pages don't lose users to slow loads. Content strategy and outreach are different specialties — I'll happily collaborate with the right team.",
        },
        {
          q: "Will my site survive a Core Update?",
          a: "Updates target specific quality signals; I can't promise immunity to all of them. What I can do is make sure your technical foundation is in top shape so you're not penalized for fixable issues — the rest is content quality and authority, which sit with you.",
        },
        {
          q: "Can you do this on Shopify / WordPress / a custom stack?",
          a: "Yes. I've optimized Shopify themes, WordPress builds, custom Next.js apps, and a fair amount of legacy PHP. The methodology is the same; the levers differ.",
        },
      ]}
    />
  );
}
